import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  directoryApi,
  type AccountSummary,
  type PendingInvitation,
} from '../client';

/**
 * Stable query-key factory for self-scoped AccountUser caches. Distinct
 * from `userKeys` (admin-scoped /users) because these endpoints are
 * keyed off the bearer-token IdpId, not the current account context —
 * cache invalidation reasoning is cleaner with its own namespace.
 */
export const accountUserKeys = {
  all: ['directory', 'account-users'] as const,
  pending: () => ['directory', 'account-users', 'pending'] as const,
};

/**
 * Fetch the caller's pending invitations. Drives the post-login prompt
 * on the AppShell. `staleTime` is short — invitations can be added by
 * an admin between two refetches, so erring on the side of fresher data
 * matters here (5 min would feel laggy if a colleague just invited you).
 */
export function usePendingInvitations(
  options?: Omit<UseQueryOptions<PendingInvitation[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<PendingInvitation[], Error>({
    queryKey: accountUserKeys.pending(),
    queryFn: () => directoryApi.getPendingInvitations(),
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Accept a pending invitation. On success:
 *  - removes the row from the pending cache (so the prompt closes)
 *  - invalidates the available-accounts list (so the new account
 *    appears in the AccountSwitcher without a refresh)
 */
export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation<AccountSummary, Error, { accountUserId: string }>({
    mutationKey: [...accountUserKeys.all, 'accept'],
    mutationFn: ({ accountUserId }) => directoryApi.acceptInvitation(accountUserId),
    onSuccess: (_summary, { accountUserId }) => {
      // Surgically remove the accepted row from the pending cache —
      // cheaper than a full refetch and keeps the prompt UI responsive.
      qc.setQueryData<PendingInvitation[]>(accountUserKeys.pending(), (prev) =>
        prev?.filter((p) => p.accountUserId !== accountUserId),
      );
      // The available-accounts query lives under @my/directory-api too —
      // we can't import its key without circular deps, but the same key
      // shape is used: ['directory', 'accounts', 'available'].
      qc.invalidateQueries({ queryKey: ['directory', 'accounts', 'available'] });
    },
  });
}
